import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import { useOnboardingStatus } from '@/hooks/useOnboardingStatus';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ShieldCheck, User, X, Shield, LifeBuoy } from 'lucide-react';
import logo from '@/assets/logo-insane-force.jpg';

// Track if user just logged in (session-level, survives re-renders but not page reloads)
let justLoggedIn = false;
let activeRoleSelectorUserId: string | null = null;

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const { isCoachOrAdmin, roles, isAdmin, isCoach, isSupport, isClient, loading: roleLoading } = useUserRole();
  const { needsOnboarding, loading: onboardingLoading } = useOnboardingStatus();
  const location = useLocation();
  const navigate = useNavigate();
  const prevUser = useRef<string | null>(null);
  const [showRoleSelector, setShowRoleSelector] = useState(false);
  const selectorShownRef = useRef(false);
  // Trigger del modal: cualquier usuario con role privilegiado
  // (admin, coach, support) que necesite escoger por dónde entrar.
  const hasPrivilegedRole = isCoachOrAdmin || isSupport;
  const shouldBlockClientViewWhileChoosingRole =
    showRoleSelector && hasPrivilegedRole && location.pathname === '/';

  useEffect(() => {
    if (!loading && user && !prevUser.current) {
      justLoggedIn = true;
    }
    if (!loading && !user) {
      justLoggedIn = false;
      activeRoleSelectorUserId = null;
    }
    prevUser.current = user?.id ?? null;
  }, [user, loading]);

  useEffect(() => {
    if (
      user &&
      !roleLoading &&
      hasPrivilegedRole &&
      location.pathname === '/' &&
      (justLoggedIn || activeRoleSelectorUserId === user.id) &&
      !selectorShownRef.current
    ) {
      justLoggedIn = false;
      activeRoleSelectorUserId = user.id;
      selectorShownRef.current = true;
      setShowRoleSelector(true);
    }
  }, [user, roleLoading, hasPrivilegedRole, roles, location.pathname]);

  const handleRoleChoice = (choice: 'coach' | 'client' | 'admin' | 'support') => {
    activeRoleSelectorUserId = null;
    setShowRoleSelector(false);
    if (choice === 'coach') {
      navigate('/coach', { replace: true });
    } else if (choice === 'admin') {
      navigate('/admin', { replace: true });
    } else if (choice === 'support') {
      navigate('/admin/soporte', { replace: true });
    }
    // 'client' → se queda en '/' (panel cliente)
  };

  if (loading || roleLoading || onboardingLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-accent/30 border-t-accent rounded-full animate-spin" />
        {showRoleSelector && renderRoleSelector()}
      </div>
    );
  }

  // Redirect unauthenticated users to /welcome instead of /auth
  if (!user) {
    return <Navigate to="/welcome" replace />;
  }

  if (shouldBlockClientViewWhileChoosingRole) {
    return renderRoleSelector();
  }

  // Pending invite token: si el usuario llegó originalmente a
  // /invite/:token, se guardó el token en localStorage. Al autenticarse
  // y aterrizar en una ruta protegida, lo regresamos a /invite/:token
  // para que pueda aceptar la invitación. InviteLandingPage limpia el
  // token al aceptar (o al final de la flow). Evita loop revisando
  // que la pathname no sea ya /invite/.
  if (!location.pathname.startsWith('/invite/')) {
    try {
      const pending = localStorage.getItem('iftmx:pending-invite-token');
      if (pending) {
        return <Navigate to={`/invite/${pending}`} replace />;
      }
    } catch { /* localStorage puede fallar en modo privado, ignorar */ }
  }

  if (needsOnboarding && !isCoachOrAdmin && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <>
      {children}
      {showRoleSelector && renderRoleSelector()}
    </>
  );

  function renderRoleSelector() {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-in fade-in duration-200">
        <div className="relative w-full max-w-sm mx-4 bg-card border-2 border-accent/30 rounded-2xl shadow-2xl shadow-accent/10 overflow-hidden">
          <button
            onClick={() => {
              activeRoleSelectorUserId = null;
              setShowRoleSelector(false);
            }}
            className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-secondary/80 transition-colors z-10"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>

          <div className="bg-gradient-to-b from-accent/10 to-transparent p-6 pb-4 text-center">
            <div className="relative inline-block mb-3">
              <div className="absolute -inset-2 bg-accent/20 rounded-full blur-lg" />
              <img src={logo} alt="Insane Force" className="relative w-16 h-16 rounded-full border-2 border-accent/50" />
            </div>
            <h2 className="text-lg font-black uppercase tracking-wider text-foreground">
              ¿Cómo deseas ingresar?
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              Selecciona tu modo de acceso
            </p>
          </div>

          <div className="p-6 pt-2 space-y-3">
            {isAdmin && (
              <button
                onClick={() => handleRoleChoice('admin')}
                className="group w-full flex items-center gap-4 p-4 rounded-xl border-2 border-amber-500/30 bg-amber-500/5 hover:border-amber-500 hover:bg-amber-500/10 transition-all"
              >
                <div className="w-12 h-12 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center group-hover:bg-amber-500/25 transition-colors">
                  <Shield className="h-6 w-6 text-amber-500" />
                </div>
                <div className="text-left flex-1">
                  <div className="font-bold text-sm uppercase tracking-wider text-amber-500">Administrador</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Gestionar usuarios, roles y sistema</div>
                </div>
              </button>
            )}

            {isCoach && (
              <button
                onClick={() => handleRoleChoice('coach')}
                className="group w-full flex items-center gap-4 p-4 rounded-xl border-2 border-accent/30 bg-accent/5 hover:border-accent hover:bg-accent/10 transition-all"
              >
                <div className="w-12 h-12 rounded-lg bg-accent/15 border border-accent/30 flex items-center justify-center group-hover:bg-accent/25 transition-colors">
                  <ShieldCheck className="h-6 w-6 text-accent" />
                </div>
                <div className="text-left flex-1">
                  <div className="font-bold text-sm uppercase tracking-wider text-accent">Coach</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Gestionar clientes, rutinas y planes</div>
                </div>
              </button>
            )}

            {/* Soporte — visible si tiene rol support, sin importar
             *  qué otros roles tenga. Aunque seas admin queremos
             *  ofrecer entrada directa al panel de soporte sin pasar
             *  por el panel admin. */}
            {isSupport && (
              <button
                onClick={() => handleRoleChoice('support')}
                className="group w-full flex items-center gap-4 p-4 rounded-xl border-2 border-purple-500/30 bg-purple-500/5 hover:border-purple-500 hover:bg-purple-500/10 transition-all"
              >
                <div className="w-12 h-12 rounded-lg bg-purple-500/15 border border-purple-500/30 flex items-center justify-center group-hover:bg-purple-500/25 transition-colors">
                  <LifeBuoy className="h-6 w-6 text-purple-500" />
                </div>
                <div className="text-left flex-1">
                  <div className="font-bold text-sm uppercase tracking-wider text-purple-500">Soporte</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Atender tickets de clientes</div>
                </div>
              </button>
            )}

            {/* Cliente — solo si tiene rol cliente. Antes se mostraba
             *  siempre, lo cual confundía a admins/support sin client
             *  role. */}
            {isClient && (
              <button
                onClick={() => handleRoleChoice('client')}
                className="group w-full flex items-center gap-4 p-4 rounded-xl border-2 border-secondary hover:border-primary/50 hover:bg-primary/5 transition-all"
              >
                <div className="w-12 h-12 rounded-lg bg-secondary border border-secondary flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                  <User className="h-6 w-6 text-foreground" />
                </div>
                <div className="text-left flex-1">
                  <div className="font-bold text-sm uppercase tracking-wider text-foreground">Cliente</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Ver mi rutina, calorías y progreso</div>
                </div>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
}
